import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { ApiError } from '../api/problem'
import type {
  BanChampion,
  BanReplay,
  ConstructorSummary,
  DriverStats,
  DriverSummary,
  F2Champion,
} from '../api/types'
import { ProblemBanner } from '../components/ProblemBanner'
import { Skeleton } from '../components/DataState'
import {
  banQuery,
  banText,
  isEmpty,
  listSavedBans,
  readBans,
  writeBans,
  type Bans,
} from '../lib/bans'
import {
  describe,
  HISTORY,
  listSaved,
  parseScale,
  problem,
  serialiseScale,
} from '../lib/scales'

/**
 * Two histories side by side: the record, a rule's replay, the bans or a
 * points table, against any other. Each side is `a` or `b` in the URL; a
 * points table rides in `pa` or `pb`, and bans in `ba` or `bb`, as the
 * `bd=…&bg=…` the ban page saves them under.
 */
export const KINDS = [
  { key: 'official', label: 'The record' },
  { key: 'f2', label: 'F2 rules' },
  { key: 'f15', label: 'F1.5 rules' },
  { key: 'reverse', label: 'Reverse' },
  { key: 'qualifying', label: 'Qualifying' },
  { key: 'ban', label: 'With Ban' },
  { key: 'points', label: 'Points table' },
] as const

export type Kind = (typeof KINDS)[number]['key']

export type Champion = {
  complete: boolean
  driver?: DriverSummary | null | undefined
  constructor?: ConstructorSummary | null | undefined
}

/** One season's champions under each side, and whether they differ. */
export type Row = {
  year: number
  a: Champion
  b: Champion
  driver: boolean
  constructor: boolean
}

/** The record and the stored rules answer as `F2Champion`; bans and tables as `BanChampion`. */
export function championsOf(
  kind: Kind,
  rows: F2Champion[] | BanChampion[],
): Map<number, Champion> {
  if (kind === 'ban' || kind === 'points' || kind === 'qualifying')
    return new Map((rows as BanChampion[]).map((row) => [row.year, row]))
  return new Map(
    (rows as F2Champion[]).map((row) => [
      row.year,
      kind === 'official'
        ? {
            complete: row.complete,
            driver: row.official_driver,
            constructor: row.official_constructor,
          }
        : { complete: row.complete, driver: row.f2_driver, constructor: row.f2_constructor },
    ]),
  )
}

/** Every season either side has, latest first. A season still being raced differs in nothing. */
export function compareChampions(a: Map<number, Champion>, b: Map<number, Champion>): Row[] {
  const years = [...new Set([...a.keys(), ...b.keys()])].sort((x, y) => y - x)
  const none: Champion = { complete: false, driver: null, constructor: null }
  return years.map((year) => {
    const left = a.get(year) ?? none
    const right = b.get(year) ?? none
    const both = left.complete && right.complete
    return {
      year,
      a: left,
      b: right,
      driver: both && left.driver?.slug !== right.driver?.slug,
      constructor: both && left.constructor?.slug !== right.constructor?.slug,
    }
  })
}

export type TitleRow = {
  driver: DriverSummary
  titles: [number, number]
  wins: [number, number]
}

/** Every driver with a title on either side, the biggest swing first. */
export function compareTitles(a: DriverStats[], b: DriverStats[]): TitleRow[] {
  const rows = new Map<string, TitleRow>()
  const add = (stats: DriverStats[], side: 0 | 1) => {
    for (const row of stats) {
      const entry = rows.get(row.driver.slug) ?? {
        driver: row.driver,
        titles: [0, 0],
        wins: [0, 0],
      }
      entry.titles[side] = row.titles
      entry.wins[side] = row.wins
      rows.set(row.driver.slug, entry)
    }
  }
  add(a, 0)
  add(b, 1)
  const swing = (row: TitleRow) => Math.abs(row.titles[1] - row.titles[0])
  return [...rows.values()]
    .filter((row) => row.titles[0] || row.titles[1])
    .sort(
      (x, y) =>
        swing(y) - swing(x) ||
        Math.max(...y.titles) - Math.max(...x.titles) ||
        x.driver.name.localeCompare(y.driver.name),
    )
}

/** Something a side can be read with: a points table, or a set of bans. */
type Choice = { value: string; name: string; detail: string }

/** The tables a side can be scored on: the same as on the points page, history then saved. */
function tables(): Choice[] {
  const choice = (name: string, text: string) => {
    const scale = parseScale(text)
    return { value: serialiseScale(scale), name, detail: describe(scale) }
  }
  return [
    ...HISTORY.map(([years, text]) => choice(`${years} table`, text)),
    ...Object.entries(listSaved())
      .sort(([x], [y]) => x.localeCompare(y))
      .map(([title, text]) => choice(title, text)),
  ]
}

/** The bans a side can be read with: each set saved on the ban page. */
function savedBans(): Choice[] {
  return Object.entries(listSavedBans())
    .sort(([x], [y]) => x.localeCompare(y))
    .map(([title, bans]) => ({
      value: banText(bans),
      name: title,
      detail: `${bans.drivers.length + bans.constructors.length + bans.grandsPrix.length} banned`,
    }))
}

type History =
  | { state: 'blocked'; message: ReactNode }
  | { state: 'pending' }
  | { state: 'error'; error: ApiError; retry: () => void }
  | {
      state: 'ready'
      stale: boolean
      champions: Map<number, Champion>
      drivers: DriverStats[]
    }

function useHistory(kind: Kind, scale: string, bans: Bans): History {
  // The record's champions ride in every stored rule's table; F2's is the one read.
  const stored =
    kind === 'official' || kind === 'f2'
      ? 'f2'
      : kind === 'f15' || kind === 'reverse'
        ? kind
        : null
  // The same keys as the category pages, so a history read there is not fetched again.
  const champions = useApi<F2Champion[]>(
    [stored ?? 'f2', 'champions'],
    stored === 'f15'
      ? () => api.GET('/api/v1/f15/champions', {})
      : stored === 'reverse'
        ? () => api.GET('/api/v1/reverse/champions', {})
        : () => api.GET('/api/v1/f2/champions', {}),
    { enabled: stored !== null },
  )
  const careers = useApi<DriverStats[]>(
    kind === 'official' ? ['stats', 'drivers'] : [kind, 'drivers'],
    kind === 'official'
      ? () => api.GET('/api/v1/stats/drivers', {})
      : kind === 'f15'
        ? () => api.GET('/api/v1/f15/drivers', {})
        : kind === 'reverse'
          ? () => api.GET('/api/v1/reverse/drivers', {})
          : () => api.GET('/api/v1/f2/drivers', {}),
    { enabled: stored !== null },
  )
  const query = banQuery(bans)
  const wrong = !scale || problem(parseScale(scale)) !== null
  // Qualifying is the server's one-word table, read under the category page's key.
  const table = kind === 'qualifying' ? 'qualifying' : scale
  const replay = useApi<BanReplay>(
    kind === 'ban' ? ['ban', query] : ['scale', table],
    kind === 'ban'
      ? () => api.GET('/api/v1/ban', { params: { query } })
      : () => api.GET('/api/v1/scale', { params: { query: { scale: table } } }),
    {
      enabled:
        (kind === 'ban' && !isEmpty(bans)) ||
        (kind === 'points' && !wrong) ||
        kind === 'qualifying',
    },
  )

  if (kind === 'ban' && isEmpty(bans)) {
    return { state: 'blocked', message: <>Choose bans for this side.</> }
  }
  if (kind === 'points' && wrong) {
    return { state: 'blocked', message: <>Choose a points table for this side.</> }
  }
  const queries = stored ? [champions, careers] : [replay]
  const failed = queries.find((entry) => entry.error)
  if (failed?.error)
    return { state: 'error', error: failed.error, retry: () => void failed.refetch() }
  if (stored ? !champions.data || !careers.data : !replay.data) return { state: 'pending' }
  return {
    state: 'ready',
    stale: queries.some((entry) => entry.isPlaceholderData),
    champions: championsOf(kind, stored ? champions.data! : replay.data!.champions),
    drivers: stored ? careers.data! : replay.data!.drivers,
  }
}

const SIDES = ['a', 'b'] as const
type Side = (typeof SIDES)[number]

function readKind(params: URLSearchParams, side: Side): Kind {
  const asked = params.get(side)
  return KINDS.find((kind) => kind.key === asked)?.key ?? (side === 'a' ? 'official' : 'f2')
}

/**
 * A side's bans as `bd=…&bg=…`, from its own `ba` or `bb`. A link from before
 * each side had its own shares `bd`/`bc`/`bg` between both.
 */
export function readSideBans(params: URLSearchParams, side: Side): string {
  const own = params.get(`b${side}`)
  return banText(readBans(own === null ? params : new URLSearchParams(own)))
}

/**
 * `params` with `side` read under `text`. Bans an older link shared are
 * handed to the other side first, so choosing for one leaves the other alone.
 */
export function writeSideBans(
  params: URLSearchParams,
  side: Side,
  text: string,
): URLSearchParams {
  const updated = writeBans(params, { drivers: [], constructors: [], grandsPrix: [] })
  const other = side === 'a' ? 'b' : 'a'
  const shared = banText(readBans(params))
  if (shared && !params.has(`b${other}`)) updated.set(`b${other}`, shared)
  if (text) updated.set(`b${side}`, text)
  else updated.delete(`b${side}`)
  return updated
}

export function ComparePage() {
  const [params, setParams] = useSearchParams()
  const kinds = { a: readKind(params, 'a'), b: readKind(params, 'b') }
  const scales = {
    a: serialiseScale(parseScale(params.get('pa') ?? '')),
    b: serialiseScale(parseScale(params.get('pb') ?? '')),
  }
  const bans = { a: readSideBans(params, 'a'), b: readSideBans(params, 'b') }
  const histories = {
    a: useHistory(kinds.a, scales.a, readBans(new URLSearchParams(bans.a))),
    b: useHistory(kinds.b, scales.b, readBans(new URLSearchParams(bans.b))),
  }
  const set = (key: string, value: string) => {
    const updated = new URLSearchParams(params)
    if (value) updated.set(key, value)
    else updated.delete(key)
    setParams(updated, { replace: true })
  }
  const banSets = savedBans()
  const choices = tables()
  const name = (side: Side) => {
    const chosen =
      kinds[side] === 'points'
        ? choices.find((table) => table.value === scales[side])
        : kinds[side] === 'ban'
          ? banSets.find((set) => set.value === bans[side])
          : undefined
    return chosen?.name ?? KINDS.find((entry) => entry.key === kinds[side])!.label
  }

  return (
    <section>
      <h1>Compare two histories</h1>
      <p className="prose muted">
        Put two versions of Formula One history side by side — the record, a rule’s replay, the
        bans or a points table — and see which seasons crown someone else.
      </p>
      <div className="compare__sides">
        {SIDES.map((side) => (
          <SidePicker
            key={side}
            side={side}
            kind={kinds[side]}
            scale={scales[side]}
            tables={choices}
            bans={bans[side]}
            banSets={banSets}
            onKind={(value) => set(side, value)}
            onScale={(value) => set(`p${side}`, value)}
            onBans={(value) => setParams(writeSideBans(params, side, value), { replace: true })}
          />
        ))}
      </div>
      {SIDES.map((side) => {
        const history = histories[side]
        if (history.state === 'blocked')
          return (
            <p key={side} className="prose">
              <span className="muted">{side.toUpperCase()}: </span>
              {history.message}
            </p>
          )
        if (history.state === 'error')
          return (
            <ProblemBanner key={side} problem={history.error.problem} onRetry={history.retry} />
          )
        return null
      })}
      {histories.a.state === 'ready' && histories.b.state === 'ready' ? (
        <div
          className={histories.a.stale || histories.b.stale ? 'state state--stale' : undefined}
        >
          <Results
            rows={compareChampions(histories.a.champions, histories.b.champions)}
            titles={compareTitles(histories.a.drivers, histories.b.drivers)}
            names={{ a: name('a'), b: name('b') }}
            only={params.get('only') === '1'}
            onOnly={(value) => set('only', value ? '1' : '')}
          />
        </div>
      ) : histories.a.state === 'pending' || histories.b.state === 'pending' ? (
        <div className="state state--loading">
          <Skeleton />
        </div>
      ) : null}
    </section>
  )
}

function SidePicker({
  side,
  kind,
  scale,
  tables,
  bans,
  banSets,
  onKind,
  onScale,
  onBans,
}: {
  side: Side
  kind: Kind
  scale: string
  tables: Choice[]
  bans: string
  banSets: Choice[]
  onKind: (kind: string) => void
  onScale: (scale: string) => void
  onBans: (bans: string) => void
}) {
  return (
    <fieldset className="compare__side">
      <legend>{side.toUpperCase()}</legend>
      <label className="search">
        <span className="muted">History</span>
        <select value={kind} onChange={(event) => onKind(event.target.value)}>
          {KINDS.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      {kind === 'points' ? (
        <Chooser
          label="Table"
          none="Choose a table"
          value={scale}
          choices={tables}
          onChange={onScale}
          to={scale ? `/points?${new URLSearchParams({ pts: scale })}` : '/points'}
          more={scale ? 'Edit it →' : 'Make one →'}
        />
      ) : null}
      {kind === 'ban' ? (
        <Chooser
          label="Bans"
          none="Choose bans"
          value={bans}
          choices={banSets}
          onChange={onBans}
          to={bans ? `/ban?${bans}` : '/ban'}
          more={bans ? 'Edit them →' : 'Make some →'}
        />
      ) : null}
    </fieldset>
  )
}

/** A side's points table or bans: one of those on offer, or whatever the link carried. */
function Chooser({
  label,
  none,
  value,
  choices,
  onChange,
  to,
  more,
}: {
  label: string
  none: string
  value: string
  choices: Choice[]
  onChange: (value: string) => void
  to: string
  more: string
}) {
  const known = choices.some((choice) => choice.value === value)

  return (
    <p>
      <label className="search">
        <span className="muted">{label}</span>
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{none}</option>
          {value && !known ? <option value={value}>From the link</option> : null}
          {choices.map((choice) => (
            <option key={`${choice.name} ${choice.detail}`} value={choice.value}>
              {choice.name}: {choice.detail}
            </option>
          ))}
        </select>
      </label>{' '}
      <Link to={to}>{more}</Link>
    </p>
  )
}

function Results({
  rows,
  titles,
  names,
  only,
  onOnly,
}: {
  rows: Row[]
  titles: TitleRow[]
  names: Record<Side, string>
  only: boolean
  onOnly: (only: boolean) => void
}) {
  const decided = rows.filter((row) => row.a.complete && row.b.complete).length
  const drivers = rows.filter((row) => row.driver).length
  const constructors = rows.filter((row) => row.constructor).length
  const shown = only ? rows.filter((row) => row.driver || row.constructor) : rows

  return (
    <>
      <p className="prose compare__summary">
        <strong>{drivers}</strong> of {decided} seasons crown a different drivers’ champion,{' '}
        <strong>{constructors}</strong> a different constructors’ champion.
      </p>
      <h2>Champions</h2>
      <label className="compare__only">
        <input
          type="checkbox"
          checked={only}
          onChange={(event) => onOnly(event.target.checked)}
        />{' '}
        Only seasons that differ
      </label>
      <div className="table-scroll">
        <table className="grid compare">
          <thead>
            <tr>
              <th scope="col" rowSpan={2}>
                Year
              </th>
              <th scope="colgroup" colSpan={2}>
                Drivers’ champion
              </th>
              <th scope="colgroup" colSpan={2}>
                Constructors’ champion
              </th>
            </tr>
            <tr>
              <th scope="col">{names.a}</th>
              <th scope="col">{names.b}</th>
              <th scope="col">{names.a}</th>
              <th scope="col">{names.b}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.year}>
                <td>
                  <Link to={`/seasons/${row.year}`}>{row.year}</Link>
                </td>
                {row.a.complete && row.b.complete ? (
                  <>
                    <Cell entity={row.a.driver} path="drivers" diff={row.driver} />
                    <Cell entity={row.b.driver} path="drivers" diff={row.driver} />
                    <Cell
                      entity={row.a.constructor}
                      path="constructors"
                      diff={row.constructor}
                    />
                    <Cell
                      entity={row.b.constructor}
                      path="constructors"
                      diff={row.constructor}
                    />
                  </>
                ) : (
                  <td colSpan={4} className="muted">
                    In progress
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Drivers’ titles</h2>
      <div className="table-scroll">
        <table className="grid compare">
          <thead>
            <tr>
              <th scope="col" rowSpan={2}>
                Driver
              </th>
              <th scope="colgroup" colSpan={3}>
                Titles
              </th>
              <th scope="colgroup" colSpan={2}>
                Wins
              </th>
            </tr>
            <tr>
              <th scope="col" className="num">
                {names.a}
              </th>
              <th scope="col" className="num">
                {names.b}
              </th>
              <th scope="col" className="num">
                Δ
              </th>
              <th scope="col" className="num">
                {names.a}
              </th>
              <th scope="col" className="num">
                {names.b}
              </th>
            </tr>
          </thead>
          <tbody>
            {titles.map((row) => {
              const delta = row.titles[1] - row.titles[0]
              return (
                <tr key={row.driver.slug}>
                  <td>
                    <Link to={`/drivers/${row.driver.slug}`}>{row.driver.name}</Link>
                  </td>
                  <td className="num">{row.titles[0]}</td>
                  <td className="num">{row.titles[1]}</td>
                  <td className={delta ? 'num compare--diff' : 'num muted'}>
                    {delta > 0 ? `+${delta}` : delta < 0 ? `−${-delta}` : '='}
                  </td>
                  <td className="num">{row.wins[0]}</td>
                  <td className="num">{row.wins[1]}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Cell({
  entity,
  path,
  diff,
}: {
  entity: { slug: string; name: string } | null | undefined
  path: 'drivers' | 'constructors'
  diff: boolean
}) {
  return (
    <td className={diff ? 'compare--diff' : undefined}>
      {entity ? (
        <Link to={`/${path}/${entity.slug}`}>{entity.name}</Link>
      ) : (
        <span className="muted">—</span>
      )}
    </td>
  )
}
